import { useState } from "react";
import { ArrowUpRight } from "@phosphor-icons/react";
import { Cog, Share2, CloudUpload, Settings2 } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { FadeIn } from "@/components/common/FadeIn";

const pillars = [
  {
    title: "Pensamiento estratégico",
    description:
      "Traducimos complejidad en prioridades claras para equipos ejecutivos y directorios.",
  },
  {
    title: "Ejecución disciplinada",
    description:
      "Aterrizamos planes en iniciativas con responsables, ritmo y métricas de avance.",
  },
  {
    title: "Resultados medibles",
    description:
      "Acompañamos con indicadores de impacto para sostener decisiones basadas en evidencia.",
  },
];

const historyMilestones = [
  {
    year: "1995",
    title: "Inicio",
    summary:
      "Comienza Pragmatic con tres socios de amplia experiencia en desarrollo informático, dando vida a esta nueva empresa.",
  },
  {
    year: "2000",
    title: "Actualizaciones",
    summary:
      "Se realizan mejoras importantes en sistemas cliente-servidor y comienza una etapa de expansión de la compañía.",
  },
  {
    year: "2014",
    title: "Internacionalización",
    summary:
      "Se abre el mercado latinoamericano con casos de éxito en México, Perú y Colombia, vigentes hasta hoy.",
  },
  {
    year: "2015",
    title: "Innovación",
    summary:
      "Se incorporan herramientas para desarrollos web y móviles, orientadas a gestión y registro de información online y offline.",
  },
  {
    year: "2018",
    title: "Clouding",
    summary:
      "Se lanza la versión cloud del ERP y se crea el área de gestión para obtener mayor rendimiento desde los datos.",
  },
  {
    year: "2020",
    title: "Versión",
    summary:
      "Se libera una nueva versión del ERP PRAGMATIC XXI, con mejores registros de movimientos, consultas y gestión móvil.",
  },
  {
    year: "2024",
    title: "Nueva versión",
    summary:
      "Se proyecta una nueva versión con mejoras de imagen, nuevas funcionalidades y desarrollos para gestión productiva.",
  },
  {
    year: "2026",
    title: "Integración IA + Robots",
    summary:
      "Se integra Chat de IA con Manuales del Sistema y Normativas vigentes. Además, un Sistema de Gestión enlazado con desarrollos en PBI y etapas con Robots para agilizar los procesos",
  },
];

const differentiators = [
  {
    id: "gestion",
    title: "Gestión",
    description:
      "Estructuramos procesos y tableros de control para convertir la operación en decisiones medibles, oportunas y alineadas al negocio. Entrega una visión panorámica y completa de la compañía.",
    Icon: Cog,
  },
  {
    id: "movilidad",
    title: "Movilidad",
    description:
      "Conectamos equipos en terreno y oficina con flujos móviles que aceleran registro, aprobación y seguimiento en tiempo real. Permite acceso seguro desde múltiples dispositivos (pc, celulares, PDA y web), otorgando movilidad y dinamismo a sus empleados.",
    Icon: Share2,
  },
  {
    id: "disponibilidad",
    title: "Disponibilidad",
    description:
      "Aseguramos continuidad operativa con soluciones en la nube, acceso seguro y alta disponibilidad para procesos críticos. Disponibiliza la información oportunamente para la toma de decisiones.",
    Icon: CloudUpload,
  },
  {
    id: "adaptabilidad",
    title: "Adaptabilidad",
    description:
      "Diseñamos plataformas flexibles que evolucionan junto a la empresa, integrando nuevos requerimientos sin fricción. agregar una animación suave de transición entre textos para que se vea aún más premium.",
    Icon: Settings2,
  },
];

export const About = () => {
  const [activeDifferentiator, setActiveDifferentiator] = useState("gestion");
  const activeInfo =
    differentiators.find((item) => item.id === activeDifferentiator) ?? differentiators[0];

  return (
    <section id="nosotros" className="py-20 md:py-28" data-testid="about-section">
      <div className="section-container grid gap-10 border-y border-[#DCE6EE] py-10 md:grid-cols-12 md:gap-12 md:py-14">
        <FadeIn className="md:col-span-5">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500" data-testid="about-kicker-text">
            Nosotros
          </p>
          <h2
            className="corporate-title-balance mt-4 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#595959]"
            data-testid="about-main-heading"
          >
            Propuesta de valor enfocada en decisiones que generan tracción real.
          </h2>
          <p className="mt-6 text-sm md:text-base text-slate-600" data-testid="about-description-text">
            En Pragmatic XXI combinamos visión de negocio, diseño de estrategia y capacidad de implementación
            para acelerar resultados sin perder foco en sostenibilidad y gobernanza.
          </p>

          <Button
            asChild
            variant="outline"
            className="mt-8 rounded-full border-[#B8C8D9] px-6 text-xs uppercase tracking-[0.15em] text-[#595959] hover:border-[#0077B6]"
            data-testid="about-cta-button"
          >
            <a href="#servicios" data-testid="about-cta-link">
              Explorar servicios <ArrowUpRight weight="bold" />
            </a>
          </Button>
        </FadeIn>

        <div className="grid gap-4 md:col-span-7 md:grid-cols-3" data-testid="about-pillars-grid">
          {pillars.map((pillar, index) => (
            <FadeIn key={pillar.title} delay={0.08 * (index + 1)}>
              <article
                className="h-full border border-[#DCE6EE] bg-white p-6 transition-transform duration-300 hover:-translate-y-1"
                data-testid={`about-pillar-card-${index + 1}`}
              >
                <p
                  className="text-xs uppercase tracking-[0.18em] text-[#7F3A9A]"
                  data-testid={`about-pillar-index-${index + 1}`}
                >
                  0{index + 1}
                </p>
                <h3 className="corporate-title-balance mt-5 text-2xl leading-tight text-slate-900" data-testid={`about-pillar-title-${index + 1}`}>
                  {pillar.title}
                </h3>
                <p className="mt-4 text-sm text-slate-600" data-testid={`about-pillar-description-${index + 1}`}>
                  {pillar.description}
                </p>
              </article>
            </FadeIn>
          ))}
        </div>

        <FadeIn className="md:col-span-12" delay={0.15}>
          <article
            className="rounded-xl border border-[#DCE6EE] bg-white p-6 md:p-8"
            data-testid="about-differentiators-section"
          >
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500" data-testid="about-differentiators-kicker">
              Qué nos diferencia
            </p>
            <h3
              className="corporate-title-balance mt-3 text-2xl md:text-3xl text-[#595959]"
              data-testid="about-differentiators-heading"
            >
              Capacidades clave de Pragmatic XXI
            </h3>

            <div className="mt-8 grid gap-5 lg:grid-cols-[1.3fr_1fr]" data-testid="about-differentiators-layout">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="about-differentiators-grid">
                {differentiators.map((item) => {
                  const Icon = item.Icon;
                  const isActive = activeDifferentiator === item.id;

                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={`rounded-xl border p-5 text-left transition-all duration-300 ${
                        isActive
                          ? "border-[#7FB7D0] bg-[#F1F8FF]"
                          : "border-[#DCE6EE] bg-[#FAFCFF] hover:border-[#AFCFE3]"
                      }`}
                      onMouseEnter={() => setActiveDifferentiator(item.id)}
                      onFocus={() => setActiveDifferentiator(item.id)}
                      onClick={() => setActiveDifferentiator(item.id)}
                      data-testid={`differentiator-card-${item.id}`}
                    >
                      <div className="mb-4 flex h-20 w-full items-center justify-center rounded-lg bg-white" data-testid={`differentiator-icon-box-${item.id}`}>
                        <Icon size={56} strokeWidth={1.8} className="text-[#6D4A8B]" data-testid={`differentiator-icon-${item.id}`} />
                      </div>
                      <p className="corporate-title-balance text-[34px] leading-none text-[#6D4A8B]" data-testid={`differentiator-title-${item.id}`}>
                        {item.title}
                      </p>
                    </button>
                  );
                })}
              </div>

              <aside
                className="rounded-xl border border-[#BCD5EA] bg-gradient-to-b from-[#F7FBFF] to-white p-6"
                data-testid="differentiator-dynamic-text-panel"
              >
                <p className="text-xs uppercase tracking-[0.2em] text-[#0077B6]" data-testid="differentiator-active-kicker">
                  Diferenciador activo
                </p>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeInfo.id}
                    initial={{ opacity: 0, y: 14, filter: "blur(10px)" }}
                    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                    exit={{ opacity: 0, y: -12, filter: "blur(8px)" }}
                    transition={{ duration: 0.62, ease: [0.22, 1, 0.36, 1] }}
                    data-testid="differentiator-animated-content"
                  >
                    <h4 className="corporate-title-balance mt-3 text-2xl text-[#005F92]" data-testid="differentiator-active-title">
                      {activeInfo.title}
                    </h4>
                    <p className="mt-4 text-sm md:text-base text-slate-600" data-testid="differentiator-active-description">
                      {activeInfo.description}
                    </p>
                  </motion.div>
                </AnimatePresence>
              </aside>
            </div>
          </article>
        </FadeIn>

        <FadeIn className="md:col-span-12" delay={0.2}>
          <div className="mt-3 border-t border-[#DCE6EE] pt-10" data-testid="about-history-section">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500" data-testid="about-history-kicker">
              Nuestra Historia
            </p>
            <h3
              className="corporate-title-balance mt-3 text-2xl md:text-3xl text-[#595959]"
              data-testid="about-history-heading"
            >
              Línea de Tiempo
            </h3>

            <div className="relative mt-8 overflow-x-auto pb-4" data-testid="about-history-timeline-scroller">
              <div
                className="absolute left-0 top-8 h-[2px] min-w-[1308px] bg-[#70AFC1]/60"
                style={{ width: "calc(100% - 220px)" }}
                data-testid="about-history-main-line-solid"
              />
              <div
                className="absolute right-0 top-8 h-0 min-w-[220px] w-[220px] border-t-2 border-dashed border-[#70AFC1]"
                data-testid="about-history-main-line-dashed"
              />

              <div className="relative flex min-w-[1528px] gap-6" data-testid="about-history-milestones-row">
                {historyMilestones.map((milestone) => {
                  const isFutureMilestone = milestone.year === "2026";

                  return (
                    <article
                      key={milestone.year}
                      className={`w-[170px] ${isFutureMilestone ? "rounded-md border border-[#A7CCE8] bg-[#EFF7FF] px-2 py-1" : ""}`}
                      data-testid={`history-milestone-card-${milestone.year}`}
                    >
                      <div className="relative h-16" data-testid={`history-milestone-top-${milestone.year}`}>
                        <div
                          className={`absolute left-1/2 top-7 h-4 w-[2px] -translate-x-1/2 ${
                            isFutureMilestone ? "border-l-2 border-dashed border-[#70AFC1]" : "bg-[#70AFC1]"
                          }`}
                          data-testid={`history-milestone-stem-${milestone.year}`}
                        />
                        <div
                          className={`absolute left-1/2 top-4 h-7 w-7 -translate-x-1/2 rounded-full ${
                            isFutureMilestone
                              ? "bg-[#005F92] border-4 border-dashed border-[#0077B6]"
                              : "bg-[#7F3A9A] border-4 border-[#70AFC1]"
                          }`}
                          data-testid={`history-milestone-dot-${milestone.year}`}
                        />
                      </div>
                      <p
                        className={`text-[28px] leading-none ${isFutureMilestone ? "text-[#0077B6] font-semibold" : "text-[#7F3A9A]"}`}
                        data-testid={`history-milestone-year-${milestone.year}`}
                      >
                        {milestone.year}
                      </p>
                      <p
                        className={`mt-2 corporate-menu-track text-xs uppercase ${isFutureMilestone ? "text-[#005F92]" : "text-slate-700"}`}
                        data-testid={`history-milestone-title-${milestone.year}`}
                      >
                        {milestone.title}
                      </p>
                      <p
                        className="mt-3 text-sm text-slate-600"
                        data-testid={`history-milestone-summary-${milestone.year}`}
                      >
                        {milestone.summary}
                      </p>
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
};
